import { useRef } from "react";
import { Shield, Sun, Moon, Languages, User, LogIn } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export default function Header({ siteName, isAdminLoggedIn, onBackToAdmin, isUserLoggedIn, userUsername, onGoToDashboard, theme, toggleTheme }) {
  const { lang, setLanguage, t } = useI18n();

  // Geste caché : appui long (~800ms) sur le logo ouvre le login admin.
  // Un clic simple garde le comportement d'origine (recharger la page).
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  const startPress = () => {
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      window.location.hash = "#/shadowpanel";
    }, 800);
  };
  const endPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  const handleLogoClick = () => {
    if (longPressFired.current) {
      longPressFired.current = false; // supprime le reload après un appui long
      return;
    }
    window.location.reload();
  };

  return (
    <header className="border-b border-zinc-800/50 bg-black/50 backdrop-blur-sm sticky top-0 z-50" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2 sm:py-4 flex items-center justify-between">
        <div className="hidden sm:block w-[140px]" />
        <div
          className="flex items-center gap-2.5 cursor-pointer group shrink-0 select-none"
          style={{ touchAction: "manipulation" }}
          onClick={handleLogoClick}
          onPointerDown={startPress}
          onPointerUp={endPress}
          onPointerLeave={endPress}
          onPointerCancel={endPress}
        >
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 group-hover:border-cyan-400/50 group-hover:shadow-lg group-hover:shadow-cyan-500/20 transition-colors duration-300 overflow-hidden">
            <img src="/icons/icon-96.png" alt="" className="w-5 h-5 sm:w-6 sm:h-6 object-contain" onError={(e) => { e.target.style.display = "none"; e.target.parentElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"/><path d="m3 7 9 6 9-6"/></svg>'; }} />
          </div>
          <span className="font-mono text-sm sm:text-lg font-bold tracking-tight whitespace-nowrap">
            <span className="text-zinc-500 group-hover:text-zinc-400 transition-colors duration-200">&lt;</span>
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent group-hover:from-cyan-300 group-hover:via-blue-300 group-hover:to-purple-300 transition-colors duration-300">{siteName}</span>
            <span className="text-zinc-500 group-hover:text-zinc-400 transition-colors duration-200"> /&gt;</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 sm:w-[140px] justify-end">
          {/* Theme toggle - more square on desktop */}
          <button
            onClick={toggleTheme}
            className="w-8 h-8 sm:w-9 sm:h-9 sm:rounded-md flex items-center justify-center rounded border border-zinc-700/50 hover:border-cyan-500/30 text-zinc-500 hover:text-cyan-400 transition-colors duration-200"
            title={theme === "dark" ? t.lightMode : t.darkMode}
            data-testid="theme-toggle"
          >
            {theme === "dark" ? (
              <Sun className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            ) : (
              <Moon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            )}
          </button>

          {/* Language selector */}
          <button
            onClick={() => setLanguage(lang === "fr" ? "en" : "fr")}
            className="h-8 sm:h-9 px-2 sm:px-2.5 sm:rounded-md flex items-center gap-1 rounded border border-zinc-700/50 hover:border-cyan-500/30 text-zinc-500 hover:text-cyan-400 transition-colors duration-200 font-mono text-[10px] sm:text-xs font-bold uppercase"
            title={lang === "fr" ? "Switch to English" : "Passer en français"}
            data-testid="lang-toggle"
          >
            <Languages className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            {lang === "fr" ? "EN" : "FR"}
          </button>

          {isUserLoggedIn ? (
            <button
              onClick={onGoToDashboard}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 transition-colors text-xs font-mono"
              data-testid="user-dashboard-btn"
            >
              <User className="w-3.5 h-3.5" />
              {userUsername}
            </button>
          ) : (
            <button
              onClick={() => { window.location.hash = "#/login"; }}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded border border-zinc-700/50 hover:border-cyan-500/30 text-zinc-400 hover:text-cyan-400 transition-colors text-xs font-mono"
              data-testid="user-login-btn"
              title={t.login}
            >
              <LogIn className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{t.login}</span>
            </button>
          )}
          {isAdminLoggedIn && (
            <button
              onClick={onBackToAdmin}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors text-xs font-mono"
              data-testid="back-to-admin-btn"
            >
              <Shield className="w-3.5 h-3.5" />
              Admin
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
